import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { experience } from "../data/portfolio";

function TimelineItem({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.14, ease: [0.23, 1, 0.32, 1] }}
      className="relative pl-10"
    >
      {/* Timeline dot */}
      <div
        className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-[#0a0a0f]"
        style={{
          background: item.accentColor,
          boxShadow: `0 0 12px ${item.accentColor}`,
        }}
      />

      {/* Card */}
      <div className="glass-card p-7 mb-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h3 className="font-display font-black text-xl tracking-tight text-white mb-1">
              {item.role}
            </h3>
            <p
              className="font-mono text-sm tracking-wide"
              style={{ color: item.accentColor }}
            >
              {item.company}
            </p>
          </div>
          <div className="text-right">
            <span className="font-mono text-xs text-white/30 tracking-wide block">{item.period}</span>
            <span
              className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-mono tracking-wide"
              style={{
                background: `${item.accentColor}15`,
                border: `1px solid ${item.accentColor}30`,
                color: item.accentColor,
              }}
            >
              {item.type}
            </span>
          </div>
        </div>

        {/* Contributions */}
        <ul className="space-y-3">
          {item.contributions.map((c, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-white/40 leading-relaxed">
              <span className="mt-1 shrink-0 font-mono text-xs" style={{ color: item.accentColor }}>
                →
              </span>
              {c}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export default function Experience() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="experience" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div className="max-w-3xl mx-auto">
        <div ref={ref}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-label mb-3"
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">04 // Experience</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4"
          >
            Where I've<br />
            <span className="grad-text">done the work.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/40 text-base mb-16"
          >
            Professional roles and the impact I've shipped in each.
          </motion.p>
        </div>

        {/* Timeline */}
        <div className="relative pl-px">
          <div className="timeline-line" />
          <div className="pt-2">
            {experience.map((item, i) => (
              <TimelineItem key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
